import "./lib/sentry.ts";

import { Group, Stack, Text } from "@mantine/core";
import { Button } from "@onengine/component-library";
import * as Sentry from "@sentry/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";

// Required for design tokens
import "./styles/global.scss";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";
// Required for Mantine components to work properly
import "@mantine/core/styles.css";
import "@mantine/tiptap/styles.css";
import "@mantine/dates/styles.css";
import "@mantine/notifications/styles.css";

import "@onengine/component-library/style.css";

// Required for Leaflet map components
import "leaflet-draw/dist/leaflet.draw.css";
import "leaflet/dist/leaflet.css";

import "./styles/styles.scss";

import FirstLoadOverlay from "@/components/features/AppLayout/FirstLoadOverlay";
import { Modal } from "@/components/ui/Modal/Modal";

import type { RouterContextType } from "./api/permissions/permissions.api.ts";
import { AppProvider } from "./providers/AppProvider.tsx";

import { routerRef } from "@/lib/routerRef";
import { setupSessionSyncResponder } from "@/lib/sessionSync";
import { queryClient } from "@/providers/QueryClientProviderWrapper.tsx";

setupSessionSyncResponder();

const CHUNK_RELOAD_KEY = "chunk-reload-ts";
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

function reloadIfOutsideCooldown() {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - last < CHUNK_RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
}

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return (
    error.message.includes("Failed to fetch dynamically imported module") ||
    error.message.includes("error loading dynamically imported module") ||
    error.message.includes("Unable to preload CSS") ||
    error.name === "ChunkLoadError"
  );
}

window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault();
  reloadIfOutsideCooldown();
});

function RouterErrorFallback({ error }: { error: Error }) {
  const chunkError = isChunkLoadError(error);

  useEffect(() => {
    if (chunkError) {
      reloadIfOutsideCooldown();
    } else {
      Sentry.captureException(error);
    }
  }, [error, chunkError]);

  if (chunkError) return <NewVersionFallback />;
  return <RootErrorFallback />;
}

function NewVersionFallback() {
  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      title="New version available"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          A new version of this application has been deployed. Please refresh the page to continue.
        </Text>
        <Group justify="flex-end">
          <Button variant="primary" size="large" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

function RootErrorFallback() {
  return (
    <Modal
      isOpen={true}
      onClose={() => {}}
      title="Something went wrong"
      withCloseButton={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      size="sm"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          An unexpected error occurred. Please refresh the page and try again. If the issue
          persists, contact support@onengine.ai
        </Text>
        <Group justify="flex-end">
          <Button variant="primary" size="large" onClick={() => window.location.reload()}>
            Refresh Page
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// Create a new router instance
const router = createRouter({
  routeTree,
  context: { queryClient } as RouterContextType,
  defaultErrorComponent: RouterErrorFallback,
  InnerWrap: FirstLoadOverlay,
  defaultPreload: "intent",
  scrollRestoration: true,
  defaultStructuralSharing: true,
  defaultPreloadStaleTime: 0,
});

routerRef.current = router;

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Temporary comment
// Dev-only MSW mocks for the file manager module. Gated behind VITE_FILE_MANAGER_MOCKS.
async function enableFileManagerMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_FILE_MANAGER_MOCKS !== "true") return;
  const { startFileManagerMocks } = await import("./mocks/fileManagerMocks.browser");
  await startFileManagerMocks();
}

// Temporary comment
// Dev-only MSW mocks for the proposals AI endpoints (Tickets 7/8/9) while the
// feat/proposals-ai backend hasn't landed. Gated behind VITE_PROPOSAL_AI_MOCKS.
async function enableProposalAiMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_PROPOSAL_AI_MOCKS !== "true") return;
  const { startProposalAiMocks } = await import("./mocks/proposalAiMocks.browser");
  await startProposalAiMocks();
}

// Temporary comment: MOCK FLAG — VITE_FILE_REGISTRY_MOCKS serves the Ticket 12 file-registry
// contract from MSW until the feat/file-registry backend is merged and running locally.
// "registry" mocks only GET /file-registry/files; "all" also mocks WO documents + uploads.
async function enableFileRegistryMocks(): Promise<void> {
  const fileRegistryMockMode = import.meta.env.VITE_FILE_REGISTRY_MOCKS;
  if (
    !import.meta.env.DEV ||
    (fileRegistryMockMode !== "registry" && fileRegistryMockMode !== "all")
  ) {
    return;
  }
  // Both mock sets register their own MSW worker over the single service-worker
  // registration; running two workers races handler registration.
  if (import.meta.env.VITE_PROPOSAL_AI_MOCKS === "true") {
    console.error(
      "[dev-mocks] VITE_PROPOSAL_AI_MOCKS and VITE_FILE_REGISTRY_MOCKS are both set — skipping file-registry mocks; enable only one mock set at a time.",
    );
    return;
  }
  const { startFileRegistryMocks } = await import("./mocks/fileRegistry/browser");
  await startFileRegistryMocks(fileRegistryMockMode);
}

// Temporary comment: MOCK FLAG — VITE_EMAIL_AI_MOCKS serves the Email AI api-service
// contract (/email-ai-api/api/internal/email/*) from MSW until a local instance of the
// escalation-system api-service exists.
async function enableEmailAiMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_EMAIL_AI_MOCKS !== "true") return;
  // Every mock set registers its own MSW worker over the single service-worker
  // registration; running two workers races handler registration.
  const fileRegistryMockMode = import.meta.env.VITE_FILE_REGISTRY_MOCKS;
  if (
    import.meta.env.VITE_PROPOSAL_AI_MOCKS === "true" ||
    fileRegistryMockMode === "registry" ||
    fileRegistryMockMode === "all"
  ) {
    console.error(
      "[dev-mocks] VITE_EMAIL_AI_MOCKS is set alongside another mock flag — skipping email-ai mocks; enable only one mock set at a time.",
    );
    return;
  }
  const { startEmailAiMocks } = await import("./mocks/emailAiMocks.browser");
  await startEmailAiMocks();
}

// Temporary comment: MOCK FLAG — VITE_PROCUREMENT_MOCKS serves the /procurement contract
// (requests, items, suppliers, payment cards) plus the minimal session/permissions endpoints
// from MSW, and seeds a mock session so the pages render with no Cognito and no backend.
async function enableProcurementMocks(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_PROCUREMENT_MOCKS !== "true") return;
  // Every mock set registers its own MSW worker over the single service-worker
  // registration; running two workers races handler registration.
  const fileRegistryMockMode = import.meta.env.VITE_FILE_REGISTRY_MOCKS;
  if (
    import.meta.env.VITE_PROPOSAL_AI_MOCKS === "true" ||
    fileRegistryMockMode === "registry" ||
    fileRegistryMockMode === "all" ||
    import.meta.env.VITE_EMAIL_AI_MOCKS === "true"
  ) {
    console.error(
      "[dev-mocks] VITE_PROCUREMENT_MOCKS is set alongside another mock flag — skipping procurement mocks; enable only one mock set at a time.",
    );
    return;
  }
  const { startProcurementMocks } = await import("./mocks/procurementMocks.browser");
  await startProcurementMocks();
}

// Render the app
const rootElement = document.getElementById("app");
if (rootElement && !rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  void enableFileManagerMocks()
    .then(() => enableProposalAiMocks())
    .then(() => enableFileRegistryMocks())
    .then(() => enableEmailAiMocks())
    .then(() => enableProcurementMocks())
    .catch((error) => {
      console.error("[dev-mocks] failed to start — continuing without mocks", error);
    })
    .finally(() => {
      root.render(
        <StrictMode>
          <Sentry.ErrorBoundary fallback={() => <RootErrorFallback />}>
            <AppProvider>
              <RouterProvider router={router} />
            </AppProvider>
          </Sentry.ErrorBoundary>
        </StrictMode>,
      );
    });
}
